import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shadcn-in-nx/ui/tooltip";
import { ShieldAlert, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const GRADE_VARIANT = { A: "success", B: "info", C: "warning", D: "error", F: "destructive" };

const SecurityProjectsTable = ({ projects, onScan, scanning, page, totalPages, totalCount, onPageChange }) => {
    const { t } = useTranslation();

    return (
        <div className="space-y-3">
            <div className="border rounded-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("security.project")}</TableHead>
                            <TableHead>{t("security.scoreGrade")}</TableHead>
                            <TableHead>{t("security.lastScan")}</TableHead>
                            <TableHead>{t("security.findings")}</TableHead>
                            <TableHead>{t("security.token")}</TableHead>
                            <TableHead>{t("security.actions")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {projects.map((p) => (
                            <TableRow key={p.workflowId}>
                                <TableCell className="font-medium">{p.name}</TableCell>
                                <TableCell>
                                    {p.grade ? (
                                        <Badge variant={GRADE_VARIANT[p.grade] || "secondary"}>
                                            {p.score} ({p.grade})
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline">{t("security.notScanned")}</Badge>
                                    )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {p.lastScan ? new Date(p.lastScan).toLocaleDateString() : "—"}
                                </TableCell>
                                <TableCell>
                                    <span className="text-sm">
                                        <span className="text-red-600">{p.errorCount}E</span>{" / "}
                                        <span className="text-yellow-600">{p.warningCount}W</span>{" / "}
                                        <span className="text-blue-600">{p.infoCount}I</span>
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {p.isTokenPublic && (
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <ShieldAlert className="h-4 w-4 text-red-500" />
                                            </TooltipTrigger>
                                            <TooltipContent>{t("security.publicToken")}</TooltipContent>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!p.stepId || scanning === p.stepId}
                                        onClick={() => onScan(p.stepId)}
                                    >
                                        <Play className="h-3 w-3 mr-1" />
                                        {scanning === p.stepId ? t("security.scanning") : t("security.scan")}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {projects.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                    {t("security.noProjects")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        {t("security.showingProjects", { from: (page - 1) * 25 + 1, to: Math.min(page * 25, totalCount), total: totalCount })}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            {page} / {totalPages}
                        </span>
                        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecurityProjectsTable;
